import { Component, OnInit, OnDestroy } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { LoginService } from 'src/app/login/login.service'
import { AppSettingsService } from '../services/app-settings.service'
import { NavigationEnd, Event } from '@angular/router'
import { catchError, filter } from 'rxjs/operators'
import { forkJoin, Observable, of, Subscription } from 'rxjs'

@Component({
  selector: 'app-advanced-settings',
  templateUrl: './advanced-settings.component.html',
  styleUrl: './advanced-settings.component.scss'
})
export class AdvancedSettingsComponent implements OnInit, OnDestroy {
  private currentUrl: string = ''
  private prevUrl: string = ''
  private routerSub: Subscription | undefined
  showProgress = false
  saving = false
  appleSettingsData: any = {}
  isGoogleServiceJsonUploading: boolean = false
  isPlistUploading: boolean = false
  plistFile: string = ''
  googleServiceJsonFile: string = ''

  firebaseSettingsForm = this.formBuilder.group({
    stacks_user_apiKey: '',
    stacks_user_authDomain: '',
    stacks_user_databaseURL: '',
    stacks_user_projectId: '',
    stacks_user_storageBucket: '',
    stacks_user_messagingSenderId: '',
    stacks_user_appId: '',
    stacks_user_measurementId: '',
    plist_filepath: '',
    google_filepath: '',
    meta_pixel_id: '',
    meta_client_token: '',
    facebook_displayName: '',
    hotjar_id: ['', Validators.pattern(/^[0-9]*$/)],
    measurement_id: ['', Validators.pattern(/^\s*(G-[A-Za-z0-9]+)?\s*$/)]
  })

  firebaseSettingsData: any = {}
  constructor(
    private formBuilder: FormBuilder,
    private appSettingsService: AppSettingsService,
    public builderService: BuilderService,
    private router: Router,
    private route: ActivatedRoute,
    public loginService: LoginService,
  ) {
    this.builderService.showUpdateProgress = true
    // Wait for sometime till the project id is retrieved from the server if it is not available yet
    let project_id = this.builderService.getSelectedProject()
    if (!project_id) {
      setTimeout(() => {
        this.getInitialSettings()
      }, 1000)
    } else {
      this.getInitialSettings()
    }
  }

  getInitialSettings = () => {
    let project_id = this.builderService.getSelectedProject()
    this.firebaseSettingsData = this.appSettingsService.getFirebaseSettings(project_id).subscribe(
      (settings: any) => {
        console.log('firebase settings ', settings)
        this.plistFile = settings.plist_filepath
        this.googleServiceJsonFile = settings.google_filepath
        this.firebaseSettingsData = settings !== null ? settings : {}
        this.builderService.showUpdateProgress = false
      },
      (err) => {
        console.log(err)
      }
    )
  }

  private loadSettings = () => {
    const project_id = this.builderService.getSelectedProject()
    this.appSettingsService.getMetaPixelSettings(project_id).subscribe((settings: any) => {
      this.firebaseSettingsForm.patchValue({
        meta_pixel_id: settings.meta_pixel_id || '',
        meta_client_token: settings.meta_client_token || '',
        facebook_displayName: settings.facebook_displayName || ''
      })
      this.builderService.showUpdateProgress = false
    })
    this.appSettingsService.getHotjarSettings(project_id).subscribe((settings: any) => {
      this.firebaseSettingsForm.patchValue({
        hotjar_id: settings.hotjar_id || ''
      })
    })
    this.appSettingsService.getGoogleAnalyticsSettings(project_id).subscribe((settings: any) => {
      this.firebaseSettingsForm.patchValue({
        measurement_id: settings.measurement_id || ''
      })
    })
  }
  ngOnInit(): void {
    this.loadSettings()
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.prevUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      });
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  onSubmit(): void {
    // Process checkout data here
    this.saving = true

    /**
     * Loop on the Values and if the value isn't changed from the UI then retrieve the current available value
     */
    let newSubmittedData: any = {}
    let submittedData: any = this.firebaseSettingsForm.value
    console.log('submittedData', submittedData)
    let retrievedData = this.firebaseSettingsData
    for (var k in submittedData) {
      if (submittedData[k] != '') {
        newSubmittedData[k] = submittedData[k]
      } else {
        newSubmittedData[k] = retrievedData[k]
      }
    }

    console.log('newSubmittedData', newSubmittedData)
    // i wanna delete the meta data from the newSubmittedData
    delete newSubmittedData.meta_pixel_id
    delete newSubmittedData.meta_client_token
    delete newSubmittedData.facebook_displayName
    delete newSubmittedData.hotjar_id
    delete newSubmittedData.measurement_id

    const saves: Observable<any>[] = [
      this.appSettingsService.setFirebaseSettings(this.builderService.selectedProject, newSubmittedData)
    ]

    const meta_data: Record<string, any> = {
      meta_pixel_id: this.firebaseSettingsForm.value.meta_pixel_id,
      meta_client_token: this.firebaseSettingsForm.value.meta_client_token,
      facebook_displayName: this.firebaseSettingsForm.value.facebook_displayName
    }

    saves.push(this.appSettingsService.setMetaPixelSettings(this.builderService.selectedProject, meta_data))

  if (this.firebaseSettingsForm.get('hotjar_id')?.valid) {
    const hotjar_data: Record<string, any> = {
      hotjar_id: this.firebaseSettingsForm.value.hotjar_id
    }

    saves.push(this.appSettingsService.setHotjarSettings(this.builderService.selectedProject, hotjar_data))
  }

  if (this.firebaseSettingsForm.get('measurement_id')?.valid) {
    const google_analytics_data: Record<string, any> = {
      measurement_id: (this.firebaseSettingsForm.value.measurement_id || '').trim().toUpperCase()
    }

    saves.push(this.appSettingsService.setGoogleAnalyticsSettings(this.builderService.selectedProject, google_analytics_data))
  }

    // Each save fails on its own, so one failure doesn't cancel the others
    forkJoin(saves.map((save) => save.pipe(catchError(() => of(null))))).subscribe(() => {
      this.saving = false
    })
  }

  generalSettingsNext = () => {
    this.onSubmit()
    if (this.builderService.projectAccess == 'webview') {
      this.builderService.setTour(false).subscribe(
        (response) => {
          this.router.navigate(['/contentsettings'], { relativeTo: this.route })
        },
        (err) => {}
      )
    } else {
      this.router.navigate(['/site-type'], { relativeTo: this.route })
    }
  }

  processFile(fileInput: any, type: string, name: string) {
    const file: File = fileInput.target.files[0]
    const reader = new FileReader()
    reader.addEventListener('load', (event: any) => {
      this.appSettingsService.uploadFile(file, this.builderService.selectedProject, type, name).subscribe(
        (res) => {
          this.builderService.showUpdateProgress = false
          if (name === 'google_filepath') {
            this.googleServiceJsonFile = file.name
          } else if (name === 'plist_filepath') {
            this.plistFile = file.name
          }
        },
        (err) => {
          this.builderService.showUpdateProgress = false
          alert('error uploading file, please contact support')
        }
      )
    })

    reader.readAsDataURL(file)
  }

  clearFileName(name: string) {
    if (name === 'google_filepath') {
      this.googleServiceJsonFile = ''
      this.firebaseSettingsForm.get('google_filepath')?.setValue(this.googleServiceJsonFile)
    } else if (name === 'plist_filepath') {
      this.firebaseSettingsForm.get('plist_filepath')?.setValue(null)
      this.plistFile = ''
    }
  }
}
